// outbox — durable FIFO queue of unsent arrival captures.
//
// The kiosk is the most network-sensitive surface in the system: it
// runs on a tablet at the school gate during the morning rush when
// the building's wifi is at its worst. Dropping an arrival because
// of a 30-second outage is a real product failure mode (parents
// don't get notified, attendance summaries are wrong). The outbox
// solves this by:
//
//   1. Generating ONE Idempotency-Key per arrival at enqueue time.
//      The key is stable across retries — the server's 10-minute
//      dedupe window then guarantees a flushed-later record is
//      `deduped: true`, never a double capture.
//   2. Persisting the queue to localStorage so the kiosk can be
//      power-cycled mid-rush without losing what hasn't yet been
//      sent.
//   3. Storing the original capture timestamp, not the flush
//      timestamp — the server uses `captured_at` to decide late/
//      on-time, so a backed-up flush still produces correct flags.
//
// FIFO ordering is preserved on flush so the morning's arrivals
// land in the order they were captured; the worker fan-out
// (Phase 4) doesn't care, but it makes the admin dashboard's
// chronological list match intuition.

import {
  newIdempotencyKey,
  type CaptureMethod,
  type RecordArrivalRequest,
} from "@sms/sdk";

const KEY = "sms.gate.outbox.v1";

export interface OutboxEntry {
  /** Stable per arrival; used as the SDK Idempotency-Key on every flush attempt. */
  id: string;
  /** RFC 3339; captured at enqueue, not at flush. */
  captured_at: string;
  request: RecordArrivalRequest;
}

export interface OutboxAPI {
  enqueue(
    req: Omit<RecordArrivalRequest, "captured_at"> & { captured_at?: string },
  ): OutboxEntry;
  peek(): OutboxEntry | null;
  remove(id: string): void;
  list(): OutboxEntry[];
  size(): number;
  clear(): void;
  subscribe(listener: () => void): () => void;
}

interface Storage {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

const memoryStorage: Storage = (() => {
  const m = new Map<string, string>();
  return {
    getItem: (k) => m.get(k) ?? null,
    setItem: (k, v) => void m.set(k, v),
    removeItem: (k) => void m.delete(k),
  };
})();

function defaultStorage(): Storage {
  if (typeof localStorage === "undefined") return memoryStorage;
  return localStorage;
}

export function createOutbox(opts: { storage?: Storage; now?: () => Date } = {}): OutboxAPI {
  const storage = opts.storage ?? defaultStorage();
  const now = opts.now ?? (() => new Date());

  function read(): OutboxEntry[] {
    const raw = storage.getItem(KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw) as OutboxEntry[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  function write(entries: OutboxEntry[]): void {
    storage.setItem(KEY, JSON.stringify(entries));
  }

  const listeners = new Set<() => void>();
  function notify() {
    for (const l of listeners) l();
  }

  return {
    enqueue(req) {
      const entry: OutboxEntry = {
        id: newIdempotencyKey(),
        captured_at: req.captured_at ?? now().toISOString(),
        request: {
          student_id: req.student_id,
          gate_id: req.gate_id,
          capture_method: (req.capture_method ?? "qr") as CaptureMethod,
          captured_at: req.captured_at ?? now().toISOString(),
        },
      };
      const next = [...read(), entry];
      write(next);
      notify();
      return entry;
    },
    peek() {
      const all = read();
      return all[0] ?? null;
    },
    remove(id) {
      const next = read().filter((e) => e.id !== id);
      write(next);
      notify();
    },
    list() {
      return read();
    },
    size() {
      return read().length;
    },
    clear() {
      storage.removeItem(KEY);
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
}
